import { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import StreakSettingsModal from "../Streak/StreakSettingsModal";
import api from "../../api/api";
import "./Navbar.css";
import { AuthContext } from "../../context/AuthContext";
import InfoModal from "../InfoModal/InfoModal";
import { SidebarContext } from "../../context/SidebarContext";
import ProBadge from "../ProBadge/ProBadge";

const NAV_LINKS = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/add-course", label: "Add Course" },
  { to: "/roadmaps", label: "Roadmaps" },
  { to: "/tracks", label: "Tracks" },
  { to: "/", label: "Explore" },
  { to: "/guide", label: "Guide" },
];

function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const { sidebarCollapsed, setSidebarCollapsed } = useContext(SidebarContext);
  const navigate = useNavigate();
  const location = useLocation();
  const profileRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [xpData, setXpData] = useState(null);
  const [showXpInfo, setShowXpInfo] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light"
  );

  // theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "light" ? "dark" : "light");

  // scroll shadow
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // xp
  useEffect(() => {
    if (user) fetchXP();
    else setXpData(null);
  }, [user]);

  const fetchXP = async () => {
    try {
      const res = await api.get("/xp");
      setXpData(res.data);
    } catch { setXpData(null); }
  };

  // close dropdown on outside click
  useEffect(() => {
    const fn = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  // Identify if we are in a sidebar-driven route
  const isSidebarRoute = [
    '/dashboard', '/tracks', '/add-course', '/roadmaps', '/profile', '/roadmap', '/project', '/skill', '/certificate'
  ].some(route => location.pathname.startsWith(route));

  // Determine if links should be hidden. 
  // We hide them ONLY if we are on a sidebar route AND the sidebar is fully open (not collapsed).
  const hideNavLinks = isSidebarRoute && !sidebarCollapsed;

  return (
    <>
      <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
        <div className="navbar-container">

          {/* Logo — stays left */}
          <div className="nav-logo">
            <Link to="/dashboard">
              <img src="/logo_learnlia_2.png" alt="Learnlia" />
            </Link>
          </div>

          {/* Everything else — pushed right */}
          <div className="nav-right">

            {/* Nav links */}
            {!hideNavLinks && (
              <div className="nav-links">
                {NAV_LINKS.map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`nav-link ${isActive(to) ? "nav-link--active" : ""}`}
                  >
                    {label}
                    {isActive(to) && <span className="nav-link-dot" />}
                  </Link>
                ))}
              </div>
            )}

            {/* XP widget */}
            {user && xpData && (
              <button
                className="xp-widget"
                onClick={() => setShowXpInfo(true)}
                title={`Level ${xpData.level} — ${xpData.xpIntoLevel}/${xpData.xpForNext} XP`}
              >
                <div className="xp-top-row">
                  <span className="xp-level-pill">Lv {xpData.level}</span>
                  <span className="xp-nums">
                    {xpData.xpIntoLevel}
                    <span className="xp-slash">/</span>
                    {xpData.xpForNext}
                  </span>
                </div>
                <div className="xp-bar-track">
                  <div className="xp-bar-fill" style={{ width: `${xpData.percent}%` }} />
                </div>
              </button>
            )}

            {/* Theme toggle */}
            <div
              className={`theme-switch ${theme === "dark" ? "dark" : ""}`}
              onClick={toggleTheme}
              role="button"
              tabIndex={0}
              aria-label="Toggle theme"
              onKeyDown={(e) => e.key === "Enter" && toggleTheme()}
            >
              <div className="switch-track">
                <div className="switch-icon sun">☀️</div>
                <div className="switch-icon moon">🌙</div>
                <div className="switch-thumb" />
              </div>
            </div>

            {/* Profile dropdown */}
            <div className="nav-profile" ref={profileRef}>
              <button
                className={`nav-avatar ${profileOpen ? "nav-avatar--open" : ""}`}
                onClick={() => setProfileOpen(v => !v)}
                aria-label="Profile menu"
              >
                {user?.name?.[0]?.toUpperCase() || "U"}
              </button>

              {profileOpen && (
                <div className="nav-dropdown">
                  <div className="nav-dropdown-header">
                    <div className="nav-dropdown-avatar">
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="nav-dropdown-info">
                      <div className="nav-dropdown-name-row">
                        <span className="nav-dropdown-name">{user?.name}</span>
                        {user?.subscription?.status === "ACTIVE" && (
                          <ProBadge size="mini" />
                        )}
                      </div>
                      <span className="nav-dropdown-email">{user?.email}</span>
                    </div>
                  </div>

                  <div className="nav-dropdown-sep" />

                  {user ? (
                    <>
                      <button
                        className="ndd-item"
                        onClick={() => { navigate("/profile"); setProfileOpen(false); }}
                      >
                        <span>👤</span> Profile
                      </button>
                      <button
                        className="ndd-item"
                        onClick={() => { setShowStreakModal(true); setProfileOpen(false); }}
                      >
                        <span>🔥</span> Streak Settings
                      </button>
                      <div className="nav-dropdown-sep" />
                      <button className="ndd-item ndd-item--logout" onClick={handleLogout}>
                        <span>↩</span> Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="ndd-item" onClick={() => navigate("/login")}>Login</button>
                      <button className="ndd-item" onClick={() => navigate("/signup")}>Sign Up</button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Hamburger */}
            <button
              className={`nav-hamburger ${(menuOpen || (!sidebarCollapsed && isSidebarRoute)) ? "nav-hamburger--open" : ""}`}
              onClick={() => {
                if (isSidebarRoute) {
                  setSidebarCollapsed(v => !v);
                } else {
                  setMenuOpen(v => !v);
                }
              }}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>

          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="nav-mobile-menu">
            {!hideNavLinks ? NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`nav-mobile-link ${isActive(to) ? "nav-mobile-link--active" : ""}`}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </Link>
            )) : (
              <div className="nav-mobile-sidebar-note" style={{ padding: "20px", color: "var(--color-text-dim)", textAlign: "center" }}>
                Close the sidebar to view top links, or use the sidebar menu for app navigation.
              </div>
            )}
          </div>
        )}
      </nav>

      {showStreakModal && (
        <StreakSettingsModal onClose={() => setShowStreakModal(false)} />
      )}
      {showXpInfo && (
        <InfoModal
          title="XP & Level System Explained"
          text="You earn XP by completing lessons. Completing more lessons increases your level. XP resets to 0 for each new level."
          onClose={() => setShowXpInfo(false)}
        />
      )}
    </>
  );
}

export default Navbar;